import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-nav-gadget>` says about itself.
 *
 * Two things worth knowing before the sections.
 *
 * WHERE IT STANDS DECIDES WHAT ITS EXAMPLE LOOKS LIKE. It lives at the FAR END of the workspace tab
 * row and its popup is aligned to its own RIGHT edge, so its example asks the stage for `alignEnd`.
 * Put the trigger at the left of a stage and the popup opens off the side of it, which would be a
 * fact about where the example stood rather than about the piece.
 *
 * IT EDITS ITS OWN COPY AND REPORTS THE RESULT. Reordering, hiding and adding all happen inside the
 * component, against copies of the lists it was handed; each one re-renders the popup and raises
 * `gadget-nav-change` carrying the whole new visible list. The host applies that to the live tab row
 * and persists it. "Reset to Default" restores the FIRST `items` the component was ever given —
 * captured the first time the property is set, and never re-captured — so a host that later hands
 * over an already-customised list does not thereby redefine what "default" means.
 */
const docs: ComponentDocs = {
  aliases: [
    'customize navigation',
    'personalize tabs',
    'edit navigation',
    'tab settings',
    'pencil icon',
    'sliders button',
  ],

  answers: [
    'let somebody reorder their own tabs',
    'hide a tab this person never uses',
    'add a destination back to the tab row',
    'put someone’s navigation back the way it started',
  ],

  // The frame's own trailing control, paired with the launcher at the other end of the same row.
  // It exists to edit the tab row, so it belongs to the tab row and nowhere else.
  placements: ['seam'],

  inputs: [
    {
      name: 'items',
      // No `attribute`: a list of nav items cannot ride an attribute. It is set as a property.
      type: 'json',
      description:
        'The navigation as it stands, as `{ id, label, href, active? }`, in display order. The first list ' +
        'set is also remembered as the default that “Reset to Default” restores.',
    },
    {
      name: 'available',
      type: 'json',
      description:
        'The items a person may add, as `{ id, label, href }`. Optional — a tab that is hidden is moved ' +
        'into this list, so it can always be added back even if nothing was supplied here.',
    },
    {
      name: 'appLabel',
      attribute: 'app-label',
      type: 'text',
      description: 'The name of the app whose navigation is being edited, woven into the popup’s subtitle.',
      default: 'this app',
    },
  ],

  events: [
    {
      name: 'gadget-nav-change',
      when: 'After any reorder, hide or add — including the one that follows a reset.',
      carries: '`{ items }` — the whole new VISIBLE navigation, in order.',
    },
    {
      name: 'gadget-nav-reset',
      when: '“Reset to Default” was chosen. A `gadget-nav-change` carrying the restored list follows it.',
      carries: 'nothing',
    },
  ],

  // No `<slot>` is rendered: the trigger is built by the component and the popup lives in a portal.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-accent', controls: 'The sliders glyph, the active row’s inset bar, and the “+ Add” text.' },
    { token: '--caos-color-accent-fill', controls: 'The Done button’s fill.' },
    { token: '--caos-color-accent-fill-hover', controls: 'The Done button’s fill under the pointer.' },
    { token: '--caos-color-accent-contrast', controls: 'The label on the Done button. White by default; a brand with a pale accent sets a darker ink here.' },
    { token: '--caos-color-border', controls: 'The trigger’s outline, the popup’s outline, and the rules under its head and over its footer.' },
    { token: '--caos-color-border-strong', controls: 'The trigger’s outline on hover, and a footer button’s on hover.' },
    { token: '--caos-color-surface', controls: 'The popup’s background, its sticky footer, and the trigger’s fill on hover.' },
    { token: '--caos-color-surface-2', controls: 'The trigger’s resting fill and a row’s hover fill.' },
    { token: '--caos-color-bg', controls: 'The fill of the “Search items to add…” box.' },
    { token: '--caos-color-text', controls: 'The title, the row labels, and the footer buttons.' },
    { token: '--caos-color-text-muted', controls: 'The subtitle, the section labels, the move/hide glyphs, and the no-matches line.' },
    { token: '--caos-color-text-ghost', controls: 'The search box’s placeholder.' },
    { token: '--caos-focus-ring', controls: 'The ring on the trigger and on every control inside the popup.' },
    { token: '--caos-font', controls: 'The typeface of the popup and its buttons.' },
    { token: '--caos-font-mono', controls: 'The section labels and the “+ Add” buttons.' },
    { token: '--caos-radius', controls: 'The popup’s corner radius.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the trigger, the small buttons and the search box.' },
    { token: '--caos-shadow-float', controls: 'The shadow that lifts the popup off the page.' },
  ],

  // No named seams. Nothing in the shadow tree carries a `part`, so the trigger and the panel it opens
  // cannot be reached from an outside stylesheet.
  parts: [],

  methods: [],

  placing:
    'It is projected into the workspace tab row’s `gadget` slot, at the far end, where it stays pinned while ' +
    'the tab set scrolls past it. It needs the row it edits: the host hands it the same items the row is ' +
    'drawing, and applies what comes back to that row and to this person’s profile. Its popup is portaled to ' +
    'the page, so the row’s sideways overflow cannot clip it.',

  whenToUse:
    'Only as the workspace frame’s customize-navigation control. It changes what ONE person sees in their own ' +
    'tab row; it is not how an administrator configures an app’s tabs for everybody.',

  insteadUse: [
    { name: 'WorkspaceTabs', when: 'You want the tab row itself rather than the control that edits it.' },
    { name: 'AppLauncher', when: 'Somebody is moving to another app rather than reshaping the row of the one they are in.' },
    { name: 'OverflowMenu', when: 'What is behind the trigger is a list of actions to run, not a list to reorder and keep.' },
  ],

  accessibility:
    'The trigger is a real button carrying `aria-haspopup="dialog"` and an `aria-expanded` kept in step with ' +
    'the popup, and the popup is a `role="dialog"` labelled “Customize Navigation”. Every move, hide and add ' +
    'control is a real button with its own accessible name (“Move up”, “Hide”, “Add Orders”), the ones that ' +
    'cannot act are genuinely `disabled` rather than merely faded, the search box is labelled, and the glyphs ' +
    'are hidden from assistive technology. Escape and a click outside dismiss it. Focus moves into the popup ' +
    'when it opens, Tab and Shift+Tab stay inside it and wrap at either end, and Escape or Done hands focus ' +
    'back to the trigger; a click outside leaves focus where the click put it. What is left to the author: a ' +
    'reorder is announced only by the row order changing, so nothing is said out loud when an item moves.',

  examples: [
    {
      key: 'customize',
      label: 'Customize Navigation',
      summary:
        'The trigger at the end of a row; pressing it opens the real popup, where the tabs can be reordered, ' +
        'hidden and added back.',
      placement: {
        component: 'caos-nav-gadget',
        inputs: {
          appLabel: 'Sales Atlas',
          items: [
            { id: 'home', label: 'Home', href: '/sales-atlas', active: true },
            { id: 'account', label: 'Accounts', href: '/sales-atlas/account' },
            { id: 'invoice', label: 'Invoices', href: '/sales-atlas/invoice' },
          ],
          available: [
            { id: 'order', label: 'Orders', href: '/sales-atlas/order' },
            { id: 'contact', label: 'Contacts', href: '/sales-atlas/contact' },
          ],
        },
      },
      stage: {
        // Customize Navigation is a tall popup (reorder, hide, add, and a Reset/Done footer); the stage
        // is sized for it rather than for the small sliders button that opens it.
        opensBelow: 456, // measured: 448px popup + 8px gap
        // It lives at the FAR END of the tab row and opens its popup aligned to its own right edge, so it
        // is shown at the end of a row — otherwise the popup opens off the side of the stage.
        alignEnd: true,
      },
    },
  ],
};

export default docs;
